import React from 'react';
import {
  calculateTrend,
  calculateOverallHeat,
  getSeasonAverage,
  getPerformanceEmoji
} from '../../utils/playerTrends';
import StatCell from './StatCell';

/**
 * PlayerStatsRow Component
 *
 * Displays a single player's stats with heat map row background and trend indicators
 * @param {Object} props
 * @param {Object} props.player - Player stat object
 * @param {'nba'|'nhl'|'mlb'|'nfl'} props.sport - Sport type
 * @param {string} props.teamColor - Team color hex code
 */
const PlayerStatsRow = ({ player, sport, teamColor = '#9CA3AF' }) => {
  // Calculate overall performance (pass sport parameter)
  const heatLevel = calculateOverallHeat(player, sport);
  const emoji = getPerformanceEmoji(heatLevel);

  // NBA trends and averages
  const pointsTrend = calculateTrend(player.points, player.season_points, player.season_games_played);
  const reboundsTrend = calculateTrend(player.total_rebounds, player.season_rebounds, player.season_games_played);
  const assistsTrend = calculateTrend(player.assists, player.season_assists, player.season_games_played);
  const stealsTrend = calculateTrend(player.steals, player.season_steals, player.season_games_played);
  const blocksTrend = calculateTrend(player.blocks, player.season_blocks, player.season_games_played);

  const pointsAvg = getSeasonAverage(player.season_points, player.season_games_played);
  const reboundsAvg = getSeasonAverage(player.season_rebounds, player.season_games_played);
  const assistsAvg = getSeasonAverage(player.season_assists, player.season_games_played);
  const stealsAvg = getSeasonAverage(player.season_steals, player.season_games_played);
  const blocksAvg = getSeasonAverage(player.season_blocks, player.season_games_played);

  // NFL trends and averages - calculate total yards and touchdowns
  const totalYards = (player.passing_yards || 0) + (player.rushing_yards || 0) + (player.receiving_yards || 0);
  const totalTouchdowns = (player.passing_touchdowns || 0) + (player.rushing_touchdowns || 0) + (player.receiving_touchdowns || 0);

  // Calculate season totals for NFL
  const seasonTotalYards = (player.season_passing_yards || 0) + (player.season_rushing_yards || 0) + (player.season_receiving_yards || 0);
  const seasonTotalTDs = (player.season_passing_tds || 0) + (player.season_rushing_tds || 0) + (player.season_receiving_tds || 0);

  const yardsTrend = calculateTrend(totalYards, seasonTotalYards, player.season_games_played);
  const touchdownsTrend = calculateTrend(totalTouchdowns, seasonTotalTDs, player.season_games_played);
  const receptionsTrend = calculateTrend(player.receptions, player.season_receptions, player.season_games_played);

  const yardsAvg = getSeasonAverage(seasonTotalYards, player.season_games_played);
  const touchdownsAvg = getSeasonAverage(seasonTotalTDs, player.season_games_played);
  const receptionsAvg = getSeasonAverage(player.season_receptions, player.season_games_played);

  // MLB trends and averages
  const hitsTrend = calculateTrend(player.hits, player.season_hits, player.season_games_played);
  const homeRunsTrend = calculateTrend(player.home_runs, player.season_home_runs, player.season_games_played);
  const rbiTrend = calculateTrend(player.rbi, player.season_rbi, player.season_games_played);
  const runsTrend = calculateTrend(player.runs, player.season_runs, player.season_games_played);

  const hitsAvg = getSeasonAverage(player.season_hits, player.season_games_played);
  const homeRunsAvg = getSeasonAverage(player.season_home_runs, player.season_games_played);
  const rbiAvg = getSeasonAverage(player.season_rbi, player.season_games_played);
  const runsAvg = getSeasonAverage(player.season_runs, player.season_games_played);

  const getHeatRowClass = (heat) => {
    const baseClass = 'transition-all duration-200 hover:bg-gray-700/50 border-b border-gray-700/50';

    switch (heat) {
      case 'hot':
        return `${baseClass} bg-gradient-to-r from-green-900/30 to-transparent border-l-4 border-green-500`;
      case 'cold':
        return `${baseClass} bg-gradient-to-r from-red-900/30 to-transparent border-l-4 border-red-500`;
      default:
        return `${baseClass} border-l-4 border-transparent`;
    }
  };

  return (
    <tr className={getHeatRowClass(heatLevel)}>
      <td className="px-4 py-4 sticky left-0 bg-gray-800">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div
              className="w-1 h-6 rounded-full"
              style={{ backgroundColor: teamColor || '#9CA3AF' }}
            />
            <span
              className="font-semibold"
              style={{
                color: teamColor || '#D1D5DB',
                textShadow: teamColor ? `0 0 8px ${teamColor}30` : 'none'
              }}
            >
              {player.player_name || `Player #${player.player_id}`}
            </span>
          </div>
          {emoji && <span className="text-lg">{emoji}</span>}
        </div>
      </td>

      {sport === 'nba' && (
        <>
          <StatCell gameStat={player.points} seasonAvg={pointsAvg} trend={pointsTrend} label="PTS" teamColor={teamColor} />
          <StatCell gameStat={player.total_rebounds} seasonAvg={reboundsAvg} trend={reboundsTrend} label="REB" teamColor={teamColor} />
          <StatCell gameStat={player.assists} seasonAvg={assistsAvg} trend={assistsTrend} label="AST" teamColor={teamColor} />
          <StatCell gameStat={player.steals} seasonAvg={stealsAvg} trend={stealsTrend} label="STL" teamColor={teamColor} />
          <StatCell gameStat={player.blocks} seasonAvg={blocksAvg} trend={blocksTrend} label="BLK" teamColor={teamColor} />
        </>
      )}

      {sport === 'nfl' && (
        <>
          <StatCell gameStat={totalYards} seasonAvg={yardsAvg} trend={yardsTrend} label="YDS" teamColor={teamColor} />
          <StatCell gameStat={totalTouchdowns} seasonAvg={touchdownsAvg} trend={touchdownsTrend} label="TD" teamColor={teamColor} />
          <StatCell gameStat={player.receptions} seasonAvg={receptionsAvg} trend={receptionsTrend} label="REC" teamColor={teamColor} />
          <td className="px-3 py-4 text-center text-sm text-gray-400 bg-gray-800/50">
            <span className="text-xs uppercase">{player.position || 'N/A'}</span>
          </td>
        </>
      )}

      {sport === 'mlb' && (
        <>
          <StatCell gameStat={player.hits} seasonAvg={hitsAvg} trend={hitsTrend} label="H" teamColor={teamColor} />
          <StatCell gameStat={player.home_runs} seasonAvg={homeRunsAvg} trend={homeRunsTrend} label="HR" teamColor={teamColor} />
          <StatCell gameStat={player.rbi} seasonAvg={rbiAvg} trend={rbiTrend} label="RBI" teamColor={teamColor} />
          <StatCell gameStat={player.runs} seasonAvg={runsAvg} trend={runsTrend} label="R" teamColor={teamColor} />
        </>
      )}

      {sport === 'nhl' && (
        <>
          <StatCell gameStat={player.goals} seasonAvg={getSeasonAverage(player.season_goals, player.season_games_played)} trend={calculateTrend(player.goals, player.season_goals, player.season_games_played)} label="G" teamColor={teamColor} />
          <StatCell gameStat={player.assists} seasonAvg={getSeasonAverage(player.season_assists, player.season_games_played)} trend={calculateTrend(player.assists, player.season_assists, player.season_games_played)} label="A" teamColor={teamColor} />
          <StatCell gameStat={player.points} seasonAvg={getSeasonAverage(player.season_points, player.season_games_played)} trend={calculateTrend(player.points, player.season_points, player.season_games_played)} label="PTS" teamColor={teamColor} />
          <StatCell gameStat={player.shots} seasonAvg={getSeasonAverage(player.season_shots, player.season_games_played)} trend={calculateTrend(player.shots, player.season_shots, player.season_games_played)} label="SOG" teamColor={teamColor} />
          <td className="px-3 py-4 text-center text-sm text-gray-300 bg-gray-800/50">
            {player.time_on_ice || '-'}
          </td>
        </>
      )}
    </tr>
  );
};

export default PlayerStatsRow;
